import { useState, useEffect } from 'react';
import ApiService from '../services/api';
import { useOfflineStorage } from './useOfflineStorage';

export const useCustomers = (searchTerm) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isOnline } = useOfflineStorage();

  useEffect(() => {
    // Se offline e non c'è termine di ricerca, mostra tutti i clienti cache
    if (!isOnline && (!searchTerm || searchTerm.length === 0)) {
      // Chiama direttamente la funzione cache senza passarla come dipendenza
      const cachedData = localStorage.getItem('customers_cache');
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
            setCustomers(data);
            return;
          }
        } catch (error) {
          console.error('Errore lettura cache clienti:', error);
        }
      }
      setCustomers([]);
      return;
    }

    // Se c'è un termine di ricerca (online o offline)
    if (searchTerm && searchTerm.length > 1) {
      setLoading(true);
      ApiService.searchCustomers(searchTerm)
        .then(setCustomers)
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setCustomers([]);
    }
  }, [searchTerm, isOnline]);

  return { customers, loading };
};

export const useProducts = (searchTerm) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isOnline } = useOfflineStorage();

  const [lastSearchTerm, setLastSearchTerm] = useState(null);
  const [lastIsOnline, setLastIsOnline] = useState(isOnline);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce effect per searchTerm
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    // Previeni chiamate inutili se i parametri non sono cambiati
    if (debouncedSearchTerm === lastSearchTerm && isOnline === lastIsOnline) {
      return;
    }

    setLastSearchTerm(debouncedSearchTerm);
    setLastIsOnline(isOnline);

    // Se non c'è termine di ricerca (sia online che offline), mostra tutti i prodotti dalla cache
    if (!debouncedSearchTerm || debouncedSearchTerm.length === 0) {
      const cachedData = localStorage.getItem('products_cache');
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
            setProducts(data);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Errore lettura cache prodotti:', error);
        }
      }
      setProducts([]);
      setLoading(false);
      return;
    }
    // Se c'è un termine di ricerca con almeno 2 caratteri
    if (debouncedSearchTerm && debouncedSearchTerm.length > 1) {
      setLoading(true);

      if (!isOnline) {
        // Modalità offline: usa cache locale con filtro
        const cachedData = localStorage.getItem('products_cache');
        if (cachedData) {
          try {
            const { data, timestamp } = JSON.parse(cachedData);
            if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
              const queryLower = debouncedSearchTerm.toLowerCase();
              const filtered = data.filter(product => {
                const code = (product.code || '').toLowerCase();
                const description = (product.description || '').toLowerCase();

                // Ricerca esatta per codice
                if (code.includes(queryLower)) {
                  return true;
                }

                // Ricerca nella descrizione: substring match O partial word match
                if (description.includes(queryLower)) {
                  return true;
                }

                // Ricerca per parole che iniziano con la query
                const words = description.split(/\s+/);
                return words.some(word => word.startsWith(queryLower));
              });
              setProducts(filtered);
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Errore lettura cache prodotti:', error);
          }
        }
        setProducts([]);
        setLoading(false);
      } else {
        // Modalità online: usa API solo con termine di ricerca valido
        ApiService.searchProducts(debouncedSearchTerm)
          .then((result) => {
            setProducts(result);
          })
          .catch((error) => {
            console.error('Errore ricerca prodotti:', error);
            setProducts([]);
          })
          .finally(() => setLoading(false));
      }
    } else {
      // Se il termine è troppo corto, resetta i prodotti
      setProducts([]);
    }
  }, [debouncedSearchTerm, isOnline, lastSearchTerm, lastIsOnline]);

  return { products, loading };
};

export const useOrders = (filters = {}) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});

  const loadOrders = async (newFilters = filters) => {
    setLoading(true);
    try {
      const response = await ApiService.getOrders(newFilters);
      setOrders(response.orders);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(filters);
  }, []);

  return { orders, loading, pagination, loadOrders };
};